/**
 * Answer → bulk-save payload mapping for the onboarding questionnaire.
 *
 * Extracted from useFillQuestionnaire under CR-6165 to keep that hook under
 * the 500-line limit.
 */
import { FilledDatasetDetail, AnswerValue } from './questionnaireTypes';
import { findQuestion } from './questionnaireVisibility';
import { toNumericAnswer } from '../../utils/answerValues';

export type AnswerPayload = {
  question_id: number;
  text_value?: string;
  number_value?: number | null;
  date_value?: string;
  boolean_value?: boolean;
  choice_values?: string[];
  matrix_values?: Record<string, string | string[]>;
  signature_data?: string;
};

/**
 * Map the answer state onto the payload the bulk-save endpoint expects.
 *
 * @param dataset   The loaded questionnaire, used to resolve question types.
 * @param source    Current answers.
 * @param skipIds   Question ids to leave out (reviewer-locked answers).
 * @returns one entry per answerable question.
 */
export function buildAnswersPayload(
  dataset: FilledDatasetDetail | null,
  source: Record<number, AnswerValue>,
  skipIds: Set<number> = new Set(),
): AnswerPayload[] {
  return Object.entries(source).map(([questionId, value]) => {
    const id = Number(questionId);
    const question = findQuestion(dataset, id);
    if (!question) return null;
    if (skipIds.has(id)) return null;

    const answerData: AnswerPayload = { question_id: id };

    switch (question.question_type) {
      case 'text': case 'textarea': case 'email': case 'phone': case 'url':
        answerData.text_value = String(value || '');
        break;
      case 'number': case 'currency': case 'percentage': case 'rating':
        answerData.number_value = toNumericAnswer(value);
        break;
      case 'yes_no':
        answerData.boolean_value = value === true || value === 'yes' || value === 'true';
        break;
      case 'date':
        answerData.date_value = (value as string) || undefined;
        break;
      case 'single_choice':
        answerData.choice_values = value ? [value as string] : [];
        break;
      case 'multiple_choice':
        answerData.choice_values = Array.isArray(value) ? value : [];
        break;
      case 'file_upload':
        answerData.text_value = String(value || '');
        break;
      case 'matrix':
        answerData.matrix_values = (value as Record<string, string | string[]>) || {};
        break;
      case 'signature':
        answerData.signature_data = String(value || '');
        break;
      default:
        answerData.text_value = String(value || '');
    }
    return answerData;
  }).filter((a): a is AnswerPayload => a !== null);
}
